<script lang="ts">
// @ts-nocheck
import { defineComponent, h } from 'vue'
import { useEventListener } from '@vueuse/core'
import {
  NOOP,
  getScrollContainer,
  getScrollDistance,
  scrollTo,
} from '@element-plus/utils'
import ElAffix from '@element-plus/components/affix'
import { useNamespace } from '@element-plus/hooks'
import { anchorEmits, anchorProps } from './anchor'
import type { AnchorInstance } from '@element-plus/components/anchor'
import type { AnchorContext } from './anchor'
import type { AnchorLinkPayload, AnchorLinkSection } from './anchor-link'

const getOffsetTop = (element: HTMLElement, container: HTMLElement): number => {
  if (!element || !container) {
    return 0
  }

  if (!element.getClientRects().length) {
    return 0
  }

  const rect = element.getBoundingClientRect()

  if (rect.width || rect.height) {
    if (container === document.documentElement) {
      return rect.top - container.clientTop
    }
    return rect.top - container.getBoundingClientRect().top
  }

  return rect.top
}

// anchor pattern
const sharpMatcherRegx = /#([^#]+)$/
interface provideContext {
  anchor: AnchorContext
  anchorContext: AnchorInstance
}

export default defineComponent({
  name: 'ElAnchor',
  components: {
    ElAffix,
  },

  provide(): provideContext {
    return {
      anchor: this.getAnchor(),
      anchorContext: this,
    }
  },
  props: anchorProps,
  emits: anchorEmits,
  expose: ['activeLink', 'links', 'handleScrollTo', 'updateInk'],

  data() {
    const links: string[] = []
    let scrollContainer: HTMLElement | Window | undefined
    return {
      links,
      scrollContainer, // default
      isVertical: this.direction === 'y',
      activeLink: '',
      isWatch: false,
      animating: false,
      affixFlag: false,
      clearScroll: NOOP,
      ns: useNamespace('anchor'),
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.scrollContainer = getScrollContainer(
        this.getContainer(),
        this.isVertical
      )
      this.clearScroll = useEventListener(
        this.scrollContainer,
        'scroll',
        this.handleScroll
      )
      this.isWatch = true
      this.handleScroll()
    })
  },

  updated() {
    this.$nextTick(() => {
      if (this.isWatch) {
        const currentContainer = getScrollContainer(
          this.getContainer(),
          this.isVertical
        )
        if (this.scrollContainer !== currentContainer) {
          this.clearScroll()
          this.scrollContainer = currentContainer
          this.clearScroll = useEventListener(
            this.scrollContainer,
            'scroll',
            this.handleScroll
          )
          this.handleScroll()
        }
      }
      this.updateInk()
    })
  },

  beforeUnmount() {
    if (this.isWatch) {
      this.clearScroll()
      this.isWatch = false
    }
  },

  methods: {
    getAnchor(): AnchorContext {
      return {
        registerLink: (link: string) => {
          if (!this.links.includes(link)) {
            this.links.push(link)
          }
        },
        unregisterLink: (link: string) => {
          const index = this.links.indexOf(link)
          if (index !== -1) {
            this.links.splice(index, 1)
          }
        },
        scrollTo: this.handleScrollTo,
      } as AnchorContext
    },

    getCurrentActiveLink(targetOffset = 0, bounds = 5) {
      const { getCurrentAnchor } = this

      if (typeof getCurrentAnchor === 'function') {
        return getCurrentAnchor() // custom highlight anchor link
      }
      const activeLink = ''
      if (typeof document === 'undefined') {
        return activeLink
      }
      const linkSections: AnchorLinkSection[] = []
      const container = this.getContainer()
      this.links.forEach((link) => {
        const sharpLinkMatch = sharpMatcherRegx.exec(link.toString())
        if (!sharpLinkMatch) {
          return
        }
        const target = document.querySelector<HTMLElement>(
          `#${sharpLinkMatch[1]}`
        )
        if (target) {
          const top = getOffsetTop(target, container as HTMLElement)
          if (top < targetOffset + bounds) {
            linkSections.push({
              link,
              top,
            })
          }
        }
      })

      if (linkSections.length) {
        const maxSection = linkSections.reduce((prev, curr) =>
          curr.top > prev.top ? curr : prev
        )
        return maxSection.link
      }
      return ''
    },

    handleScrollTo(link: string) {
      const { targetOffset } = this

      this.setCurrentActiveLink(link)
      const container = this.getContainer()
      const scrollTop = getScrollDistance(container as HTMLElement, true)
      const sharpLinkMatch = sharpMatcherRegx.exec(link)
      if (!sharpLinkMatch) {
        return
      }
      const targetElement = document.querySelector<HTMLElement>(
        `#${sharpLinkMatch[1]}`
      )
      if (!targetElement) {
        return
      }

      const eleOffsetTop = getOffsetTop(targetElement, container as HTMLElement)
      let y = scrollTop + eleOffsetTop
      y -= targetOffset || 0
      this.animating = true

      scrollTo(y, {
        callback: () => {
          this.animating = false
        },
        getContainer: this.getContainer,
      })
    },

    setCurrentActiveLink(link: string) {
      if (this.activeLink !== link) {
        this.activeLink = link
        this.$emit('change', link)
      }
    },

    handleScroll() {
      if (this.animating) {
        return
      }
      const { bounds, targetOffset } = this
      const currentActiveLink = this.getCurrentActiveLink(
        targetOffset || 0,
        bounds
      )
      this.setCurrentActiveLink(currentActiveLink)
    },

    handleClick(payload: AnchorLinkPayload) {
      this.$emit('click', payload)
    },

    updateInk() {
      if (typeof document === 'undefined') {
        return
      }
      const activeLinkSelector = `.${this.ns.b('link')}.${this.ns.is('active')}`
      const linkTitleSelector = `.${this.ns.be('link', 'title')}`
      const linkNode = this.$el.querySelectorAll(
        `${activeLinkSelector} ${linkTitleSelector}`
      )[0]
      if (linkNode && this.$refs.dotNode) {
        ;(this.$refs.dotNode as HTMLElement).style.top = `${
          linkNode.offsetTop + linkNode.clientHeight / 2 - 6
        }px`
      }
    },

    getContainer(): HTMLElement {
      let container: HTMLElement | null = null
      if (this.target) {
        container = document.querySelector<HTMLElement>(this.target) ?? null
        if (!container) throw new Error(`Target is not existed: ${this.target}`)
      } else {
        container = document.documentElement // default scroll container
      }
      return container as HTMLElement
    },

    handleAffixChange(fixed: boolean): void {
      this.affixFlag = fixed
      this.$emit('affix-change', fixed)
    },
  },

  render() {
    const {
      affixFlag,
      showDot,
      activeLink,
      target,
      targetOffset,
      affix,
      affixOffset,
      affixPosition,
    } = this

    const wrapperStyle = {
      maxHeight: targetOffset ? `calc(100vh - ${targetOffset}px)` : '100vh',
      ...this.wrapperStyle,
    }

    const wrapperClass = {
      [`${this.ns.b()}`]: true,
      [`${this.ns.e('wrapper')}`]: true,
      [`${this.ns.is('fixed')}`]: affix && affixFlag,
      [`${this.ns.m('without-dot')}`]: !showDot,
    }

    const anchorClass = {
      [`${this.ns.e('content')}`]: true,
    }

    const dotClass = {
      [`${this.ns.e('dot')}`]: true,
      [`${this.ns.is('visible')}`]: !!activeLink,
    }

    const defaultSlot =
      this.$slots && this.$slots.default ? this.$slots.default() : ''

    const anchorContent = h(
      'div',
      {
        class: wrapperClass,
        style: wrapperStyle,
      },
      [
        h(
          'div',
          {
            class: anchorClass,
          },
          [
            h(
              'div',
              {
                class: `${this.ns.e('ink')}`,
              },
              [
                h(
                  'span',
                  {
                    class: dotClass,
                    ref: 'dotNode',
                  },
                  []
                ),
              ]
            ),
            defaultSlot,
          ]
        ),
      ]
    )

    return affix
      ? h(
          ElAffix,
          {
            target,
            offset: affixOffset,
            position: affixPosition || 'top',
            onChange: this.handleAffixChange,
          },
          { default: () => anchorContent }
        )
      : anchorContent
  },
})
</script>
